import { h } from "preact";
import { useEffect } from "preact/hooks";
import {
  AccessibilityOptionsAction,
  AccessibilityOptionsState,
  TranscriptType,
} from "./accessibilityOptionsReducer";

const TRANSCRIPT_TYPE_OPTIONS = [
  { value: "verbatim", display: "Verbatim" },
  { value: "summarized", display: "Summarized" },
];

const FONT_SIZE_OPTIONS = [
  { value: "14px", display: "14" },
  { value: "18px", display: "18" },
  { value: "24px", display: "24" },
  { value: "30px", display: "30" },
  { value: "36px", display: "36" },
  { value: "42px", display: "42" },
  { value: "48px", display: "48" },
  { value: "50px", display: "50" },
  { value: "76px", display: "76" },
  { value: "100px", display: "100" },
  { value: "124px", display: "124" },
  { value: "150px", display: "150" },
];

const FONT_FAMILY_OPTIONS = [
  { value: "arial, sans-serif", display: "Arial" },
  { value: "comic sans ms, comic sans, cursive", display: "Comic Sans" },
  { value: "courier new, courier, monospace", display: "Courier New" },
  { value: "georgia, serif", display: "Georgia" },
  { value: "tahoma, sans-serif", display: "Tahoma" },
  { value: "times new roman, serif", display: "Times New Roman" },
  { value: "trebuchet ms, sans-serif", display: "Trebuchet" },
  { value: "verdana, sans-serif", display: "Verdana" },
];

const THREEPLAY_BLUE = "#007EB5";
const COLOR_OPTIONS = [
  { value: "white", display: "White" },
  { value: "black", display: "Black" },
  { value: "#EC0B0B", display: "Red" },
  { value: "#850303", display: "Dark Red" },
  { value: "#FF7D0C", display: "Orange" },
  { value: "#FFE50C", display: "Yellow" },
  { value: "#05C121", display: "Green" },
  { value: "#047114", display: "Dark Green" },
  { value: THREEPLAY_BLUE, display: "Blue" },
  { value: "#040E71", display: "Dark Blue" },
  { value: "#66069A", display: "Purple" },
  { value: "#E82D8C", display: "Pink" },
  { value: "#D8D7D8", display: "Grey" },
  { value: "#E6D8C7", display: "Tan" },
];

const DISPLAY_OPTIONS = [
  { value: "standard", display: "Standard" },
  { value: "fullscreen", display: "Fullscreen" },
];

export interface AccesibilityMenuAttributes {
  accessibilityOptionsState: AccessibilityOptionsState;
  accessibilityOptionsDispatch: (action: AccessibilityOptionsAction) => void;
}

/**
 * The Accessibility Menu component
 */
export function AccessibilityMenu({
  accessibilityOptionsDispatch,
  accessibilityOptionsState,
}: AccesibilityMenuAttributes) {
  useEffect(() => {
    // TODO: Detect if in iframe. If so, hide/disable the fullscreen option
    const handleFullScreenChange = () => {
      accessibilityOptionsDispatch({
        type: "CHANGED_DISPLAY_MODE",
        newValue: document.fullscreenElement ? "fullscreen" : "standard",
      });
    };
    document.addEventListener("fullscreenchange", handleFullScreenChange);

    return () =>
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
  }, []);

  return (
    <div>
      <form
        id="live-asr-accessibility-menu"
        style={{
          color: accessibilityOptionsState.color,
          backgroundColor: accessibilityOptionsState.backgroundColor,
        }}
      >
        {accessibilityOptionsState.showTranscriptType && (
          <span>
            <label htmlFor="transcript-type">Transcript:</label>
            <select
              id="transcript-type"
              onChange={(e) =>
                accessibilityOptionsDispatch({
                  type: "CHANGED_TRANSCRIPT_TYPE",
                  newValue: (e.target as HTMLInputElement)
                    .value as TranscriptType,
                })
              }
              value={accessibilityOptionsState.transcriptType}
            >
              {TRANSCRIPT_TYPE_OPTIONS.map((opt) => (
                <option
                  value={opt.value}
                  key={opt.value}
                  disabled={
                    accessibilityOptionsState.transcriptType === opt.value
                  }
                >
                  {opt.display}
                </option>
              ))}
            </select>
          </span>
        )}
        <span>
          <label htmlFor="font-size">Font Size:</label>
          <select
            id="font-size"
            onChange={(e) =>
              accessibilityOptionsDispatch({
                type: "CHANGED_FONT_SIZE",
                newValue: (e.target as HTMLInputElement).value,
              })
            }
            value={accessibilityOptionsState.fontSize}
          >
            {FONT_SIZE_OPTIONS.map((opt) => (
              <option value={opt.value} key={opt.value}>
                {opt.display}
              </option>
            ))}
          </select>
        </span>
        <span>
          <label htmlFor="font-family">Font Family:</label>
          <select
            id="font-family"
            onChange={(e) =>
              accessibilityOptionsDispatch({
                type: "CHANGED_FONT_FAMILY",
                newValue: (e.target as HTMLInputElement).value,
              })
            }
            value={accessibilityOptionsState.fontFamily}
          >
            {FONT_FAMILY_OPTIONS.map((opt) => (
              <option value={opt.value} key={opt.value}>
                {opt.display}
              </option>
            ))}
          </select>
        </span>
        <span>
          <label htmlFor="font-color">Font Color:</label>
          <select
            id="font-color"
            onChange={(e) =>
              accessibilityOptionsDispatch({
                type: "CHANGED_FONT_COLOR",
                newValue: (e.target as HTMLInputElement).value,
              })
            }
            value={accessibilityOptionsState.color}
          >
            {COLOR_OPTIONS.map((opt) => (
              <option
                value={opt.value}
                key={opt.value}
                disabled={
                  accessibilityOptionsState.backgroundColor === opt.value
                }
              >
                {opt.display}
              </option>
            ))}
          </select>
        </span>
        <span>
          <label htmlFor="background-color">Background Color:</label>
          <select
            id="background-color"
            onChange={(e) =>
              accessibilityOptionsDispatch({
                type: "CHANGED_BACKGROUND_COLOR",
                newValue: (e.target as HTMLInputElement).value,
              })
            }
            value={accessibilityOptionsState.backgroundColor}
          >
            {COLOR_OPTIONS.map((opt) => (
              <option
                value={opt.value}
                key={opt.value}
                disabled={accessibilityOptionsState.color === opt.value}
              >
                {opt.display}
              </option>
            ))}
          </select>
        </span>
        <span>
          <label htmlFor="display-mode">Display:</label>
          <select
            id="display-mode"
            onChange={(e) => {
              if ((e.target as HTMLInputElement).value === "fullscreen") {
                document.querySelector("#live-asr-target")?.requestFullscreen();
              }
            }}
            value={accessibilityOptionsState.displayMode}
          >
            {DISPLAY_OPTIONS.map((opt) => (
              <option
                value={opt.value}
                key={opt.value}
                disabled={accessibilityOptionsState.displayMode === opt.value}
              >
                {opt.display}
              </option>
            ))}
          </select>
        </span>
      </form>
      <hr />
    </div>
  );
}
